import { trigger } from '@angular/animations';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Component, OnDestroy, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { GalleryDto, TenantDto } from '@picflow/types';
import { Subscription } from 'rxjs';
import { ArchiveDownloadInfoComponent } from '../../../account/components/archive-download-info/archive-download-info.component';
import { fadeOutIf } from '../../../animations/fade-out.if.animation';
import { fadeIf } from '../../../animations/fade.if.animation';
import { AssetTransitionerComponent } from '../../../gallery-shared/components/asset-transitioner/asset-transitioner.component';
import { AccessLevelService } from '../../../services/access-level.service';
import { BrowserService } from '../../../services/browser.service';
import { PrintOverviewService } from '../../../services/print-overview.service';
import { StateService } from '../../../services/state.service';
import { TranslateService } from '../../../services/translate.service';
import { UpdateService } from '../../../services/update.service';
import { VersionCheckService } from '../../../services/version-check.service';
import { PrintOverviewComponent } from '../../../shared-extra/components/print-overview/print-overview.component';
import { UploadInfoComponent } from '../../../upload-info/components/upload-info/upload-info.component';
import { PicflowBadgeComponent } from '../../components/picflow-badge/picflow-badge.component';

@Component({
  selector: 'app-view-gallery-base-page',
  templateUrl: './view-gallery-base-page.component.html',
  styleUrls: ['./view-gallery-base-page.component.scss'],
  animations: [trigger('loader', fadeOutIf), trigger('picflowBadge', fadeIf)],
  standalone: true,
  // changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    AssetTransitionerComponent,
    PicflowBadgeComponent,
    UploadInfoComponent,
    ArchiveDownloadInfoComponent,
    PrintOverviewComponent,
  ],
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: { ngSkipHydration: 'true' },
})
export class ViewGalleryBasePageComponent implements OnDestroy {
  private platformId = inject<string>(PLATFORM_ID);
  public state = inject(StateService);
  private updateService = inject(UpdateService);
  private route = inject(ActivatedRoute);
  public printOverviewService = inject(PrintOverviewService);
  private router = inject(Router);
  private versionCheckService = inject(VersionCheckService); // just here to allow verison checks to happen
  private browserService = inject(BrowserService);
  private accessLevelService = inject(AccessLevelService);
  private translateService = inject(TranslateService);
  browserPlatform: boolean;
  showLoader = true;
  routeDataSub: Subscription;
  gallery: GalleryDto;
  tenant: TenantDto;
  showPicflowBranding: boolean;
  onAssetPage: boolean;

  constructor() {
    // loading here to avoid loading en.json only when first text comes into view
    if (isPlatformServer(this.platformId)) {
      this.gallery = this.route.snapshot.data.gallery;
      this.tenant = this.gallery?.tenant as TenantDto;
      return;
    }
    this.router.events.subscribe(event => {
      if (!(event instanceof NavigationEnd)) return;
      this.onAssetPage = this.browserService.urlIsAssetUrl(location.href);
    });
    this.routeDataSub = this.route.data.subscribe(async data => {
      if (this.gallery) this.updateService.unsubscribeGallery(this.gallery.id);
      this.gallery = data.gallery;
      this.tenant = this.gallery?.tenant as TenantDto;

      const shouldSubscribe = await this.shouldSubscribe();
      if (shouldSubscribe) {
        this.updateService.subscribeGallery(this.gallery.id);
      }
    });
    this.browserPlatform = isPlatformBrowser(this.platformId);
    if (this.browserPlatform) setTimeout(() => (this.showLoader = false), 500);
    this.showPicflowBranding = this.gallery?.['tenant_product_name'] === 'Picflow Free';
  }

  private async shouldSubscribe() {
    const accessLevel = await this.accessLevelService.getAccessLevel(this.gallery);
    return accessLevel !== 'view_only';
  }

  ngOnDestroy() {
    if (isPlatformServer(this.platformId)) return;
    this.routeDataSub.unsubscribe();
    if (this.gallery) this.updateService.unsubscribeGallery(this.gallery.id);
  }
}
